import { sceneManager, setIsActive } from ".."
import { toggleShortcutsPopup } from "./ShortcutHandler"
import { renderMenuItems, objectActions } from "./ObjectMenu"
import { openExternal } from "./Helpers"
import { options } from "../SceneManager"

const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
const cmd = (key, shift = false) =>
	isMac ? `${shift ? "⇧" : ""}⌘${key}` : `Ctrl+${shift ? "Shift+" : ""}${key}`

const isActive = (selector) => document.querySelector(selector).classList.contains("is-active")

const selectedSubject = () => {
	const subject = sceneManager.selectedManager.subject
	return subject && subject.alive ? subject : null
}

const openUrl = (url) => () => openExternal(url)

// Items are rendered by renderMenuItems (see ObjectMenu.js for the format)
const listMenus = {
	"#edit-menu": () => [
		{ label: "Undo", shortcut: cmd("Z"), action: () => sceneManager.stateManager.undo() },
		{ label: "Redo", shortcut: cmd("Z", true), action: () => sceneManager.stateManager.redo() },
		"-",
		// The selected object's actions, same as its right-click menu
		...objectActions(selectedSubject()),
		"-",
		{ label: "Clear scene", action: () => sceneManager.clearScene() },
	],
	"#view-menu": () => [
		{ label: "Rotate", shortcut: "Q", action: () => sceneManager.setGizmo("rotate"), checked: () => isActive("#rotate-gizmo") },
		{ label: "Move", shortcut: "W", action: () => sceneManager.setGizmo("translate"), checked: () => isActive("#translate-gizmo") },
		{ label: "Scale", shortcut: "E", action: () => sceneManager.setGizmo("scale"), checked: () => isActive("#scale-gizmo") },
		{ label: "Hide tools", shortcut: "R", action: () => sceneManager.setGizmo("hidden"), checked: () => isActive("#hidden-gizmo") },
		"-",
		{ label: "Mirror posing", shortcut: "J", action: () => sceneManager.toggleMirrorPosingMode(document.querySelector("#mirror-posing")), checked: () => isActive("#mirror-posing") },
		{ label: "Lock camera", shortcut: "L", action: () => sceneManager.lockCamera(document.querySelector("#lock-camera")), checked: () => isActive("#lock-camera") },
		"-",
		{ label: "Focus on selection", shortcut: "F", action: () => sceneManager.setRotationPoint(selectedSubject()), enabled: () => !!selectedSubject() },
		{ label: "Restore camera position", shortcut: "C", action: () => sceneManager.setCameraPosition() },
		{ label: "Save camera position", shortcut: isMac ? "⇧C" : "Shift+C", action: () => sceneManager.saveCameraPosition() },
		"-",
		{ type: "slider", label: "Ambient lighting", min: 0, max: 60, step: 0.5, value: () => options.ambientLight, onInput: (value) => sceneManager.setAmbientLightIntensity(value) },
		{ type: "slider", label: "Spot lighting", min: 0, max: 10, step: 0.5, value: () => options.directionalLight, onInput: (value) => sceneManager.setDirectionalLightIntensity(value) },
		{ type: "slider", label: "Field of view", min: 45, max: 175, step: 1, value: () => options.fov, onInput: (value) => sceneManager.setFieldOfView(value) },
		{ label: "Reset light", action: () => sceneManager.resetLight() },
	],
	"#help-menu": () => [
		{ label: "Getting started", action: () => setIsActive("#welcome-popup") },
		{ label: "Keyboard shortcuts", shortcut: "?", action: () => toggleShortcutsPopup() },
		"-",
		{ label: "Documentation", action: openUrl("https://justsketch.me/docs/") },
		{ label: "FAQ", action: openUrl("https://justsketch.me/faq/") },
		{ label: "Report a bug", action: openUrl("https://justsketch.me/bugs/") },
		{ label: "Suggest a feature", action: openUrl("https://justsketch.me/contact/") },
		"-",
		// Reopens the redesign note, which is signed and links to the contact page
		{ label: "A note from Herman", action: () => setIsActive("#redesign-popup") },
	],
}

const renderListMenu = (selector) => {
	renderMenuItems(document.querySelector(selector), listMenus[selector]())
}

// A title that was just opened by hovering. Clicking it right after should keep
// it open rather than toggle it shut, so that click is swallowed.
let hoverOpened = null

document.addEventListener("click", (event) => {
	if (hoverOpened && event.target.closest(".menubar-title") === hoverOpened) {
		event.stopPropagation()
	}
	hoverOpened = null
}, true)

document.querySelectorAll(".menubar-title").forEach((title) => {
	title.addEventListener("pointerleave", () => {
		if (hoverOpened === title) hoverOpened = null
	})

	const selector = title.getAttribute("onclick").match(/'(#[\w-]+)'/)[1]

	if (listMenus[selector]) {
		// The inline handler has toggled the menu by now; refresh check marks and disabled states
		title.addEventListener("click", () => renderListMenu(selector))
	}

	// Like a native menu bar: once a menu is open, hovering another title switches to it.
	// Mouse only: a tap fires an emulated enter before its click, which would toggle twice.
	title.addEventListener("pointerenter", (event) => {
		if (event.pointerType != "mouse") return
		const open = document.querySelector(".menubar-title.is-active")
		if (open && open !== title) {
			title.click()
			hoverOpened = title
		}
	})
})

// On narrow screens the menus scroll sideways; fade the right edge while there's more to see
const menus = document.querySelector(".menubar-menus")
const updateMenusFade = () => {
	menus.classList.toggle("has-more", menus.scrollLeft + menus.clientWidth < menus.scrollWidth - 1)
}
menus.addEventListener("scroll", updateMenusFade, { passive: true })
// Watching the bar and its titles catches window resizes, styles and fonts loading, and translation
const menusObserver = new ResizeObserver(updateMenusFade)
menusObserver.observe(menus)
menus.querySelectorAll(".menubar-title").forEach((title) => menusObserver.observe(title))

// On narrow screens Upgrade leads the menu titles; on wide ones it ends the toolbar
const upgradeItem = document.querySelector("#upgrade-button").parentElement
const compactLayout = window.matchMedia("(max-width: 1023px)")
const placeUpgrade = () => {
	if (compactLayout.matches) menus.prepend(upgradeItem)
	else document.querySelector(".menubar-tools").append(upgradeItem)
}
placeUpgrade()
compactLayout.addEventListener("change", placeUpgrade)
